import { useState, useEffect, useCallback } from 'react';
import { Unlock, ChevronDown, ChevronRight } from 'lucide-react';
import SheetModal, { SheetNadpisSkupiny } from '../../components/SheetModal';
import { SheetAlert } from '../MojeSmena/SheetUI';
import { SHEET_TOKENS, getTextColors } from '../MojeSmena/sheetStyle';
import VolnyFlek from './VolnyFlek';

const AKCENT = '#0CC0DF';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const sklonujVolne = (n) => (n === 1 ? 'volná směna' : (n >= 2 && n <= 4 ? 'volné směny' : 'volných směn'));

/** „dnes" / „zítra" / „so 26. 9." — orientace bez počítání. */
function popisDne(datum) {
    const den = new Date(`${datum}T00:00:00`);
    const dnes = new Date();
    const rozdil = Math.round((den - new Date(dnes.getFullYear(), dnes.getMonth(), dnes.getDate())) / 86400000);
    const label = den.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
    if (rozdil === 0) return { label, doplnek: 'dnes' };
    if (rozdil === 1) return { label, doplnek: 'zítra' };
    if (rozdil <= 7) return { label, doplnek: `za ${rozdil} ${rozdil <= 4 ? 'dny' : 'dní'}` };
    return { label, doplnek: null };
}

/** Fleky seskupené po dnech, v pořadí, v jakém přijdou z backendu (datum + čas). */
function poDnech(fleky) {
    const mapa = new Map();
    fleky.forEach(f => {
        if (!mapa.has(f.datum)) mapa.set(f.datum, []);
        mapa.get(f.datum).push(f);
    });
    return [...mapa.entries()];
}

/**
 * Celý seznam volných směn k rozebrání — otevírá se z hlavičky Mého měsíce.
 *
 * Ukáže **zobrazený měsíc**, ať seznam sedí s chipem „N volných k rozebrání"
 * nad kalendářem — dřív chip počítal měsíc, seznam 90 dní, a čísla si odporovala.
 * Co leží za hranicí měsíce, se nezahodí: patčka dole řekne, kolik toho je,
 * a jedním klepnutím obzor rozšíří na 90 dní.
 *
 * Nahoře stojí to, co vzít můžu — kvůli rychlé orientaci. Fleky, na které
 * kvalifikace nestačí, se nezahazují, ale schovají se pod rozbalovátko, aby
 * seznam neředily.
 */
const MESICE = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen',
    'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

export default function VolneSmenySheet({ onClose, onZmena, isDark, rok, mesic }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [beruId, setBeruId] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [ostatniOtevrene, setOstatniOtevrene] = useState(false);
    // Rozšířený obzor si vyžádá člověk sám — výchozí je měsíc pod prstem.
    const [siroky, setSiroky] = useState(false);

    const barvy = getTextColors(isDark);
    const maMesic = !siroky && rok > 0 && mesic >= 1 && mesic <= 12;

    const nacti = useCallback(() => {
        setLoading(true);
        const dotaz = maMesic ? `?rok=${rok}&mesic=${mesic}` : '?dni=90';
        return fetch(`/api/me/volne-smeny${dotaz}`)
            .then(r => (r.ok ? r.json() : null))
            .then(setData)
            .catch(() => setChyba('Seznam se nepodařilo načíst.'))
            .finally(() => setLoading(false));
    }, [maMesic, rok, mesic]);

    useEffect(() => { nacti(); }, [nacti]);

    const vzit = async (flek) => {
        setBeruId(flek.id);
        setChyba(null);
        try {
            const res = await fetch(`/api/shifts/${flek.id}/prevzit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ zamestnanec_id: data?.zamestnanec_id ?? undefined }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d?.error || `Chyba ${res.status}`);
            }
            onZmena?.();       // překreslí kalendář pod sheetem
            await nacti();     // a vlastní seznam — flek z něj zmizí
        } catch (e) {
            setChyba(e.message || 'Směnu se nepodařilo převzít.');
        } finally {
            setBeruId(null);
        }
    };

    const fleky = data?.fleky || [];
    const moje = fleky.filter(f => f.muzu);
    const ostatni = fleky.filter(f => !f.muzu);
    const pocet = moje.length;
    const dalsiPocet = data?.dalsi_pocet ?? 0;
    const obdobiLabel = maMesic ? `${MESICE[mesic - 1]} ${rok}` : 'nejbližších 90 dní';

    return (
        <SheetModal
            onClose={onClose}
            isDark={isDark}
            ikona={Unlock}
            barva={AKCENT}
            nadpis="Volné směny"
            podnadpis={loading ? 'Načítám…'
                : `${pocet} ${sklonujVolne(pocet)} pro tebe · ${obdobiLabel}`}
        >
            {chyba && <SheetAlert kind="error" isDark={isDark}>{chyba}</SheetAlert>}

            {!loading && pocet === 0 && (
                <div style={{ padding: '24px 8px', textAlign: 'center', color: barvy.textMuted }}>
                    <Unlock size={26} style={{ opacity: 0.4, marginBottom: 8 }} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: barvy.textPrimary, marginBottom: 3 }}>
                        Teď není co vzít
                    </div>
                    <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                        {maMesic && dalsiPocet > 0
                            ? `V ${obdobiLabel} už nic nezbývá — dál ale ano.`
                            : 'Dáme vědět, jakmile vznikne flek na tvoji kvalifikaci.'}
                    </div>
                </div>
            )}

            {poDnech(moje).map(([datum, denFleky]) => {
                const { label, doplnek } = popisDne(datum);
                return (
                    <div key={datum} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <SheetNadpisSkupiny isDark={isDark} style={{ margin: '2px 2px 0' }}>
                            {label}{doplnek ? ` · ${doplnek}` : ''}
                        </SheetNadpisSkupiny>
                        {denFleky.map(f => (
                            <VolnyFlek key={f.id} flek={f} onVzit={vzit} beru={beruId === f.id} isDark={isDark} />
                        ))}
                    </div>
                );
            })}

            {ostatni.length > 0 && (
                <>
                    <button type="button" onClick={() => setOstatniOtevrene(v => !v)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                        background: 'none', border: 'none', padding: '6px 2px', cursor: 'pointer',
                        fontSize: SHEET_TOKENS.typography.hint.size, fontWeight: 700,
                        letterSpacing: 0.4, textTransform: 'uppercase', color: barvy.textMuted,
                    }}>
                        {ostatniOtevrene ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        Nemůžeš vzít ({ostatni.length})
                    </button>
                    {ostatniOtevrene && ostatni.map(f => {
                        const { label } = popisDne(f.datum);
                        return (
                            <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ fontSize: 10.5, color: barvy.textMuted, paddingLeft: 2 }}>{label}</span>
                                <VolnyFlek flek={f} onVzit={vzit} isDark={isDark} />
                            </div>
                        );
                    })}
                </>
            )}
            {maMesic && dalsiPocet > 0 && (
                <button type="button" onClick={() => setSiroky(true)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    width: '100%', marginTop: 10, padding: '10px 12px', cursor: 'pointer',
                    borderRadius: 10, border: `1px dashed ${AKCENT}55`, background: 'transparent',
                    fontSize: 12.5, fontWeight: 600, color: AKCENT,
                }}>
                    <ChevronDown size={14} />
                    {dalsiPocet === 1 ? 'Ještě' : dalsiPocet <= 4 ? 'Další' : 'Dalších'}{' '}
                    {dalsiPocet} {sklonujVolne(dalsiPocet)} později — ukázat
                </button>
            )}

            {siroky && (
                <button type="button" onClick={() => setSiroky(false)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    width: '100%', marginTop: 10, padding: '8px 12px', cursor: 'pointer',
                    borderRadius: 10, border: 'none', background: 'transparent',
                    fontSize: 12, fontWeight: 600, color: barvy.textMuted,
                }}>
                    <ChevronRight size={13} />
                    Zpět na zobrazený měsíc
                </button>
            )}
        </SheetModal>
    );
}
